import { QueryClient } from "@tanstack/react-query";
import { useState } from "react";

// WHY THIS FILE EXISTS: on the server `@tanstack/query-core` sets gcTime to Infinity, so the old
// module-scope client in +Layout.tsx, shared by every SSR request, retained every store page ever
// rendered (prod OOM on the 384MB heap after ~300 distinct pages, 2026-10-04). It also served the
// first-seen version of a store, because `initialData` is ignored once the key is cached. Never
// reintroduce a module-scope client for SSR: the server gets a new client per render, the browser
// keeps one singleton per tab.

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 1000 * 60 * 5,
      },
    },
  });

let browserQueryClient: QueryClient | undefined;

export const resolveQueryClient = (): QueryClient => {
  if (typeof window === "undefined") return createQueryClient();
  if (!browserQueryClient) browserQueryClient = createQueryClient();
  return browserQueryClient;
};

// Lazy initializer (function reference, not a call): each SSR render tree gets its own client,
// the browser gets the singleton even across Layout remounts.
export const useRequestQueryClient = (): QueryClient => {
  const [client] = useState(resolveQueryClient);
  return client;
};
